import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Avatar, If, Ring, Text } from 'src/Components'
import Phone from 'src/Components/Phone'
import NMLS from 'src/Components/NMLS'
import Email from 'src/Components/Email'
import {
  IconLaptop,
  IconLinkedIn,
  IconMapMarker,
  IconMobile,
  IconPaperPlane
} from 'src/Components/Icon'
import { dispatchEvent } from 'src/helpers/tagManager'

import styles from './CardPro.module.scss'

const propTypes = {
  companyName: PropTypes.string,
  customerEmail: PropTypes.string,
  customerLinkedin: PropTypes.string,
  customerName: PropTypes.string,
  customerNmls: PropTypes.string,
  customerPhone: PropTypes.string,
  customerPhoto: PropTypes.string,
  customerStateLicense: PropTypes.string,
  customerTitle: PropTypes.string,
  customerWebsite: PropTypes.string,
  officeAddress: PropTypes.shape({
    city: PropTypes.string,
    street: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }),
  officeName: PropTypes.string,
  officeNmls: PropTypes.string
}

const CardPro = ({
  companyName,
  customerEmail,
  customerLinkedin,
  customerName,
  customerNmls,
  customerPhone,
  customerPhoto,
  customerStateLicense,
  customerTitle,
  customerWebsite,
  officeAddress,
  officeName,
  officeNmls
}) => {
  return (
    <div
      className='card-section cards-tiled cards-tiled--half-width'
      data-qa='CardPro'
    >
      <div className={styles.container}>
        <If condition={customerPhoto}>
          <Photo imageUri={customerPhoto} websiteUri={customerWebsite} />
        </If>

        <Text.Headline size='xl'>
          <If condition={customerWebsite}>
            <a href={customerWebsite}>{customerName}</a>
          </If>
          <If condition={!customerWebsite}>
            <span>{customerName}</span>
          </If>
        </Text.Headline>

        <If condition={customerTitle}>
          <Text color='pink' paragraph>
            {customerTitle}
          </Text>
        </If>
        <If condition={companyName}>
          <Text paragraph>{companyName}</Text>
        </If>
        <Text
          element='ul'
          color='quiet'
          align='center'
          className={styles.office}
        >
          <If condition={officeName}>
            <li>{officeName}</li>
          </If>
          <If condition={officeNmls}>
            <li>
              <NMLS value={officeNmls} />
            </li>
          </If>
        </Text>

        <ContactInfo
          address={officeAddress}
          email={customerEmail}
          phone={customerPhone}
          websiteUri={customerWebsite}
          linkedinUri={customerLinkedin}
        />

        <If condition={customerNmls || customerStateLicense}>
          <Credentials
            nmls={customerNmls}
            stateLicense={customerStateLicense}
          />
        </If>
      </div>
    </div>
  )
}

const Photo = ({ imageUri, websiteUri }) => {
  const avatar = (
    <Ring className={styles.ring}>
      <Avatar className={styles.avatar} src={imageUri} />
    </Ring>
  )

  if (websiteUri) {
    return (
      <a href={websiteUri} className={styles.photo}>
        {avatar}
      </a>
    )
  }

  return <span className={styles.photo}>{avatar}</span>
}

const ContactLink = ({ track, ...props }) => (
  <Text
    element='a'
    color='quiet'
    size='s'
    onClick={() => dispatchEvent(track)}
    {...props}
  />
)

const ContactInfo = ({ address, email, phone, websiteUri, linkedinUri }) => {
  return (
    <Text element='ul' align='center'>
      <Address address={address} />
      <If condition={phone}>
        <li className={styles.contactItem}>
          <Phone
            value={phone}
            iconStart={IconMobile}
            className={styles.contact}
          />
        </li>
      </If>
      <If condition={email}>
        <li className={styles.contactItem}>
          <Email
            value={email}
            iconStart={IconPaperPlane}
            className={styles.contact}
          />
        </li>
      </If>
      <If condition={websiteUri}>
        <li className={styles.contactItem}>
          <ContactLink href={websiteUri}>
            <IconLaptop className={styles.icon} />
            <FormattedMessage id='CardPro.website' defaultMessage='Website' />
          </ContactLink>
        </li>
      </If>
      <If condition={linkedinUri}>
        <li className={styles.contactItem}>
          <ContactLink href={linkedinUri}>
            <IconLinkedIn className={styles.icon} />
            LinkedIn
          </ContactLink>
        </li>
      </If>
    </Text>
  )
}

const Address = ({ address }) => {
  const { street, city, state, zipCode, zip } = address || {}
  const cityStateZip = `${city}, ${state} ${zipCode || zip}`

  return (
    <If condition={street && city && state && (zipCode || zip)}>
      <li className={styles.contactItem}>
        <ContactLink
          href={`http://maps.google.com/?q=${street}, ${cityStateZip}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          <IconMapMarker className={styles.icon} /> {street}
          <br />
          {cityStateZip}
        </ContactLink>
      </li>
    </If>
  )
}

const Credentials = ({ nmls, stateLicense }) => {
  return (
    <Text element='ul' align='center' size='s' className={styles.creds}>
      <NMLS element='li' value={nmls} />
      <If condition={stateLicense}>
        <li>
          <FormattedMessage
            id='CardPro.stateLicense'
            defaultMessage='State License:'
          />{' '}
          {stateLicense}
        </li>
      </If>
    </Text>
  )
}

CardPro.propTypes = propTypes

export default CardPro
